import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { listHrContracts, listPayrollMovements } from '../../api/hr';
import { fetchMultiYearPayroll } from './payrollEvolution';
import { formatMoney, formatNumber } from '../../utils/format';
import PageHeader from '../../components/PageHeader';
import './HrPage.css';
import './socialChargesGrid.css';
import './payrollEvolution.css';

/* People → Planification du personnel → Evolution pluriannuelle.
   Trajectoire de la masse salariale sur N-1 → N+5 : brut, charges, net, coût
   employeur, EPT moyen, effectif. Années > N = taux de charges de N (« estimé »,
   décision Olivier 2026-06-28). Données issues du même moteur que l'onglet Masse
   salariale planifiée → l'année N coïncide exactement. */

const PAST = 1;   // années d'historique (N-1)
const FUTURE = 5; // années de projection (N+1 → N+5)

export default function PayrollEvolutionPage({ activeTenant, year, embedded, unit = 'chf' }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const N = year || new Date().getFullYear();
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF';

  const [contracts, setContracts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([
      listHrContracts(tenantId),
      listPayrollMovements(tenantId),
    ])
      .then(([c, m]) => {
        if (!c.error) setContracts(c.data || []);
        if (!m.error) setMovements(m.data || []);
      })
      .finally(() => setLoading(false));
  }, [tenantId, N]);

  const years = useMemo(
    () => Array.from({ length: PAST + 1 + FUTURE }, (_, i) => N - PAST + i),
    [N],
  );

  /* Plans annuels CALCULÉS PAR LE SERVEUR (un appel par année ; N+1 → N+5 avec
     les taux de N, « estimé »). Le moteur JS a été migré le 2026-08-21. */
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!tenantId) { setRows([]); return; }
    fetchMultiYearPayroll({ tenantId, contracts, movements, years, estimatedFrom: N + 1, ratesYear: N })
      .then(setRows).catch(() => setRows([]));
  }, [tenantId, contracts, movements, years, N]);

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">Select a tenant first.</div></main>;

  const hasRates = rows.some((r) => r.year === N && r.hasRates);
  const cur = (v) => formatMoney(v, unit, 0);
  const yLabel = (y) => (y < N ? `${y}` : y === N ? `${y}` : `${y}`);

  // Lignes du tableau synthétique (libellé, accès, variante de style, type).
  const lines = [
    { label: 'Salaires bruts', get: (r) => r.gross, variant: 'strong', kind: 'money' },
    { label: t('phrases:p_0834336deb'), get: (r) => -r.chargesEmpl, variant: 'cost', kind: 'money' },
    { label: '= Salaire net', get: (r) => r.net, variant: 'strong', kind: 'money' },
    { label: 'Charges employeur', get: (r) => r.chargesEmpr, variant: 'empr', kind: 'money' },
    { label: p('= Coût employeur'), get: (r) => r.cout, variant: 'strong', kind: 'money' },
    { label: 'EPT moyen', get: (r) => r.fteAvg, variant: '', kind: 'fte', sep: true },
    { label: 'Effectif (fin)', get: (r) => r.hcEnd, variant: '', kind: 'int' },
    { label: t('phrases:p_21c92dab87'), get: (r) => r.costPerFte, variant: 'muted', kind: 'money' },
  ];
  const fmtCell = (kind, v) => {
    if (kind === 'fte') return formatNumber(v, 1);
    if (kind === 'int') return formatNumber(v, 0);
    return cur(v);
  };

  const chartData = rows.map((r) => ({
    year: r.year,
    cout: Math.round(r.cout),
    fte: Number(r.fteAvg.toFixed(2)),
    estimated: r.estimated,
  }));

  const ChartTip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload || {};
    return (
      <div className="pev-tip">
        <div className="pev-tip__year">{label}{d.estimated ? p(' · estimé') : ''}</div>
        <div className="pev-tip__row"><span className="pev-tip__sw pev-tip__sw--cost" />{p('Coût employeur :')}&nbsp;<b>{cur(d.cout)}</b> {chfLbl}</div>
        <div className="pev-tip__row"><span className="pev-tip__sw pev-tip__sw--fte" />EPT moyen&nbsp;: <b>{formatNumber(d.fte, 1)}</b></div>
      </div>
    );
  };

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && <PageHeader title="Evolution pluriannuelle" />}

      {loading && <div className="hr-page__loading">Loading…</div>}
      {!loading && !hasRates && (
        <p className="hr-page__empty">Aucune charge sociale saisie pour {N}. Saisis les taux dans Input People → Charges sociales.</p>
      )}
      {!loading && hasRates && (
        <div className="pev">
          {/* Tableau synthétique à gauche */}
          <section className="scs pev__table">
            <div className="scs__head">
              <span className="scs__title">Masse salariale · {N - PAST} → {N + FUTURE}</span>
              <span className="scs__sub">N+1 → N+5 : taux de {N} · {chfLbl}</span>
            </div>
            <div className="scs__scroll">
              <table className="scs__table">
                <thead>
                  <tr>
                    <th className="scs__lbl scs__lbl--head"> </th>
                    {rows.map((r) => (
                      <th key={r.year} className={`scs__mh${r.year === N ? ' pev__yh--cur' : ''}`}>
                        {yLabel(r.year)}
                        {r.estimated && <span className="pev__est">est.</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln) => (
                    <tr
                      key={ln.label}
                      className={`scs__row${ln.variant ? ` scs__row--${ln.variant}` : ''}${ln.sep ? ' pev__row--sep' : ''}`}
                    >
                      <td className="scs__lbl">{ln.label}</td>
                      {rows.map((r) => (
                        <td key={r.year} className={`scs__cell${r.year === N ? ' pev__cell--cur' : ''}`}>
                          {fmtCell(ln.kind, ln.get(r))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Graphique combo à droite */}
          <section className="scs pev__chart">
            <div className="scs__head">
              <span className="scs__title">{p('Coût employeur & EPT')}</span>
              <span className="scs__sub">barres = coût ({chfLbl}) · courbe = EPT</span>
            </div>
            <div className="pev__chart-body">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 14, right: 12, left: 6, bottom: 4 }}>
                  <CartesianGrid stroke="#EDE8DF" vertical={false} />
                  <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#57534E' }} tickLine={false} axisLine={{ stroke: '#E7E5E4' }} />
                  <YAxis
                    yAxisId="cost"
                    tick={{ fontSize: 11, fill: '#57534E' }}
                    tickFormatter={(v) => formatNumber(unit === 'kchf' ? v / 1000 : v, 0)}
                    width={unit === 'kchf' ? 56 : 76}
                    axisLine={false}
                    tickLine={false}
                    label={{ value: chfLbl, angle: -90, position: 'insideLeft', offset: 14, fontSize: 11, fill: '#78716C' }}
                  />
                  <YAxis
                    yAxisId="fte"
                    orientation="right"
                    tick={{ fontSize: 11, fill: '#B8862A' }}
                    width={40}
                    axisLine={false}
                    tickLine={false}
                    label={{ value: 'EPT', angle: 90, position: 'insideRight', offset: 10, fontSize: 11, fill: '#B8862A' }}
                  />
                  <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(43,32,62,0.05)' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="cost" dataKey="cout" name={t('phrases:p_99313d1f57')} fill="#2B203E" radius={[3, 3, 0, 0]} maxBarSize={46}>
                    {chartData.map((d) => (
                      <Cell key={d.year} fillOpacity={d.estimated ? 0.4 : 0.9} />
                    ))}
                  </Bar>
                  <Line
                    yAxisId="fte"
                    type="monotone"
                    dataKey="fte"
                    name="EPT moyen"
                    stroke="#B8862A"
                    strokeWidth={2.4}
                    dot={{ r: 3, fill: '#B8862A' }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
